'use client';
import CartCrossSellItem from './cartCrossSellItem';
import WindowSize from '@/utils/windowSize';
import { productDecode } from '@/lib/shopify/gid';
import { useCartToRender } from '@/components/commerce/cart/cart-context';
import { useEffect, useState } from 'react';
import { getRecommendationsFromCart } from './recommendation-utils';

export default function CartCrossSellFull({
  cartFromServer,
  initialRecommendations,
  block,
  crossSell,
  position,
  children
}) {
  const cart = useCartToRender(cartFromServer);
  const windowSize = WindowSize();
  const { background_color, _metadata } = block.cart_cross_sell_full;
  const [recommendations, setRecommendations] = useState(
    initialRecommendations
  );

  // Keep recommendations updated with the cart
  useEffect(() => {
    if (cart?.lines?.length > 0 && cart?.isOptimistic) {
      const getNewRecommendations = async () => {
        const newRecs = await getRecommendationsFromCart(cart);
        setRecommendations(newRecs);
      };
      getNewRecommendations();
    }
  }, [cart]);

  // Filter and sort product recommendations
  const filteredProducts = recommendations.products
    .filter((product) => !product.outOfStock) // Filter out products that are out of stock
    .map((product) => {
      // Find in-stock variants that match the cartProductSize
      const matchingVariants =
        product.variants?.filter(
          (variant) =>
            !variant.outOfStock &&
            variant.selectedOptions.some(
              (option) =>
                option.name === 'Size' &&
                option.value
                  .toLowerCase()
                  .split('/')
                  .map((v) => v.trim())
                  .includes(recommendations.cartProductSize)
            )
        ) || [];

      // If no matching variants, exclude the product
      return matchingVariants.length > 0
        ? { ...product, variants: matchingVariants }
        : null;
    })
    .filter(Boolean) // Remove null products
    .sort((a, b) => {
      const highestPriceA = Math.max(
        ...a.variants.map((variant) => Number(variant.price.amount) || 0)
      );
      const highestPriceB = Math.max(
        ...b.variants.map((variant) => Number(variant.price.amount) || 0)
      );
      return highestPriceB - highestPriceA;
    });

  const cartProductIds = cart?.lines?.map((productItem) =>
    productDecode(productItem.merchandise.product.id)
  );

  const recommendedProducts = filteredProducts.filter((product) => {
    return !cartProductIds.includes(productDecode(product.id));
  });

  // Show children if no cross-sell products are available
  if (recommendedProducts.length === 0 || cart?.lines?.length === 0) {
    return <>{children}</>;
  }

  return (
    <section
      id={`${_metadata.uid}_${position}`}
      className={`cart-cross-sell-full bg-[${background_color}] py-15`}
      data-name={'cart-cross-sell-full'}
    >
      <div className="h4 mb-10 text-center">{crossSell?.title}</div>
      <div className="max-w-container mx-auto flex flex-col justify-center px-4 sm:px-10 lg:flex-row lg:px-0">
        {recommendedProducts.map((item, index) => (
          <CartCrossSellItem
            product={item}
            recommendedProducts={recommendedProducts}
            cartList={cart.lines}
            index={index}
            key={index}
            windowSize={windowSize}
            crossSell={crossSell}
          />
        ))}
      </div>
    </section>
  );
}
